import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { CymbioSpinner } from '../../components/common/Spinner';
import ApproveVariantsModal from '../../components/modals/ApproveVariantsModal';
import ProductsPermissions from '../../components/blocks/ProductsPermissions';

import {
  setRetailerProductsSearch,
  getRetailerProducts,
  getRetailerProductsFilters,
  retailerProductsCheck,
  retailerProductsUncheck,
  setRetailerProductsCurrentFilter,
  setRetailerProductsOffset,
  retailerProductsApprove,
  retailerProductsUnapprove,
  retailerProductsApproveChecked,
  retailerProductsUnapproveChecked,
  retailerProductsSaveVariants,
  retailerProductsCheckAllOnPage,
  retailerProductsCheckAll,
  retailerProductsUncheckAll,
  setRetailerProductsModalOpen,
  setRetailerProductsModalClose,
  setRetailerProductsOrder,
} from '../../store/actions/retailerProducts';

import { setPermissionsTab } from '../../store/actions/permissions';
import { setProductFiltersSearch } from '../../store/actions/productFilters';

function ProductsPermissionsContainer(props) {
  const {
    setTab,
    permissionType,
    unselectAll,
    getFilters,
    getProducts,
    id,
    modal,
    canUserDoActions,
    isManagedByCymbio,
  } = props;

  useEffect(() => {
    setTab(permissionType);
    return () => unselectAll();
  }, [setTab, permissionType, unselectAll]);

  useEffect(() => {
    getFilters(id);
    getProducts(id, true);
  }, [getFilters, getProducts, id]);

  const shouldAllowUserToDoActions = useMemo(() => {
    if (window.location.href.includes('/retailers')) {
      if (isManagedByCymbio) {
        return !!canUserDoActions;
      }
    }
    return true;
  }, [isManagedByCymbio, canUserDoActions]);

  const selectAllOnPage = () => {
    const selected = {};
    props.data.forEach((item) => {
      selected[item.model] = item.variants;
    });
    props.selectAllOnPage(selected);
  };

  if (props.isLoading) {
    return <CymbioSpinner />;
  }

  return (
    <div>
      {modal && (
        <ApproveVariantsModal
          open={modal}
          onClose={props.closeModal}
          permissionType={permissionType}
          {...props}
          shouldAllowUserToDoActions={shouldAllowUserToDoActions}
          isVerboseInventoryInfo
        />
      )}

      <ProductsPermissions
        {...props}
        selectAllOnPage={selectAllOnPage}
        shouldAllowUserToDoActions={shouldAllowUserToDoActions}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  const {
    data,
    count,
    perPage,
    checked,
    approved,
    productDetails,
    search,
    offset,
    modal,
    product,
    isLoading,
    isLoadingProducts,
    order,
    orderBy,
  } = state.retailerProducts;
  const { user } = state.role;
  const autoSync = state.retailer?.current?.auto_sync;
  const { filters, currentFilters, currentFilterSearches } = state.productFilters;
  const canUserDoActions = user?.is_super_admin && ['support', 'admin'].includes(user?.role);
  const isManagedByCymbio = data.length && data[0].managed_by_cymbio;

  return {
    autoSync,
    count,
    data,
    perPage,
    checked,
    approved,
    productDetails,
    searchQuery: search,
    filters,
    currentFilters,
    currentFilterSearches,
    offset,
    modal,
    product,
    order,
    orderBy,
    isLoading,
    isLoadingProducts,
    pageCount: Math.ceil(count / perPage),
    selectedCount: Object.keys(checked).length,
    canUserDoActions,
    isManagedByCymbio,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  setTab: (tab) => dispatch(setPermissionsTab(tab)),
  setPage: (offset) => dispatch(setRetailerProductsOffset(offset)),
  getProducts: (id) => dispatch(getRetailerProducts(id, false)),
  getFilters: (id) => dispatch(getRetailerProductsFilters(id)),
  approve: (variants) => dispatch(retailerProductsApprove(variants)),
  unapprove: (variants) => dispatch(retailerProductsUnapprove(variants)),
  approveChecked: () => dispatch(retailerProductsApproveChecked(ownProps.id)),
  unapproveChecked: () => dispatch(retailerProductsUnapproveChecked(ownProps.id)),
  productSearch: (search) => dispatch(setRetailerProductsSearch(search)),
  check: (id) => dispatch(retailerProductsCheck(id)),
  uncheck: (id) => dispatch(retailerProductsUncheck(id)),
  setFilter: (filter) => dispatch(setRetailerProductsCurrentFilter(filter)),
  setFilterSearch: (filter, search) => dispatch(setProductFiltersSearch(filter, search)),
  saveVariants: () => dispatch(retailerProductsSaveVariants(ownProps.id)),
  selectAllOnPage: (ids) => dispatch(retailerProductsCheckAllOnPage(ids)),
  selectAll: () => dispatch(retailerProductsCheckAll(ownProps.id)),
  unselectAll: () => dispatch(retailerProductsUncheckAll),
  openModal: (product) => dispatch(setRetailerProductsModalOpen(product)),
  closeModal: () => dispatch(setRetailerProductsModalClose),
  setOrder: (order, orderBy) => dispatch(setRetailerProductsOrder(order, orderBy)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsPermissionsContainer);

ProductsPermissionsContainer.defaultProps = {
  checked: {},
};

ProductsPermissionsContainer.propTypes = {
  permissionType: PropTypes.string.isRequired,
  perPage: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  filters: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  getFilters: PropTypes.func.isRequired,
  getProducts: PropTypes.func.isRequired,
  productSearch: PropTypes.func.isRequired,
  check: PropTypes.func.isRequired,
  uncheck: PropTypes.func.isRequired,
  checked: PropTypes.object,
  setFilter: PropTypes.func.isRequired,
  setFilterSearch: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  selectAllOnPage: PropTypes.func.isRequired,
  modal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
};
